/*
    🔥 VALIDADORES

    Cada uno recibe (valor, parámetro, todosLosValores) y devuelve un
    mensaje de error o null.

    El tercer argumento es lo que permite reglas cruzadas: "repetí la
    contraseña" necesita ver el otro campo.
*/

const IS_EMPTY = (v) => (
    v === null || v === undefined || v === ''
    || (Array.isArray(v) && v.length === 0)
    || (typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date) && Object.keys(v).length === 0)
);

export const VALIDATORS = {

    required: (value, _param) => (IS_EMPTY(value) ? 'Este campo es obligatorio' : null),

    email: (value) => {
        if (IS_EMPTY(value)) return null;
        return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) ? null : 'Ingresá un correo válido';
    },

    url: (value) => {
        if (IS_EMPTY(value)) return null;
        try { new URL(value); return null; } catch { return 'Ingresá una URL válida'; }
    },

    date: (value) => {
        if (IS_EMPTY(value)) return null;
        const text = String(value).trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return 'Ingresá una fecha válida';
        const [year, month, day] = text.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));
        const valid = date.getUTCFullYear() === year
            && date.getUTCMonth() === month - 1
            && date.getUTCDate() === day;
        return valid ? null : 'Ingresá una fecha válida';
    },

    port: (value) => {
        if (IS_EMPTY(value)) return null;
        const port = Number(value);
        return Number.isInteger(port) && port >= 1 && port <= 65535
            ? null
            : 'El puerto debe estar entre 1 y 65535';
    },

    min: (value, param) => {
        if (IS_EMPTY(value)) return null;
        return Number(value) >= param ? null : `El mínimo es ${param}`;
    },

    max: (value, param) => {
        if (IS_EMPTY(value)) return null;
        return Number(value) <= param ? null : `El máximo es ${param}`;
    },

    minLength: (value, param) => {
        if (IS_EMPTY(value)) return null;
        return String(value).length >= param ? null : `Mínimo ${param} caracteres`;
    },

    maxLength: (value, param) => {
        if (IS_EMPTY(value)) return null;
        return String(value).length <= param ? null : `Máximo ${param} caracteres`;
    },

    minItems: (value, param) => {
        if (IS_EMPTY(value)) return null;
        return (value?.length ?? 0) >= param ? null : `Elegí al menos ${param}`;
    },

    maxItems: (value, param) => {
        if (IS_EMPTY(value)) return null;
        return (value?.length ?? 0) <= param ? null : `Como máximo ${param}`;
    },

    pattern: (value, param) => {
        if (IS_EMPTY(value)) return null;
        const { regex, message } = param instanceof RegExp ? { regex: param, message: null } : param;
        return regex.test(value) ? null : (message ?? 'El formato no es válido');
    },

    // 🔥 Reglas cruzadas: compara contra otro campo
    sameAs: (value, param, values) => {
        const { field, message } = typeof param === 'string' ? { field: param, message: null } : param;
        return value === values[field] ? null : (message ?? 'Los valores no coinciden');
    },

    // 🔥 Tu propia función. Devuelve un string para fallar, null para pasar.
    custom: (value, param, values) => param?.(value, values) ?? null,
};

/*
    🔥 VALIDA UN CAMPO CONTRA SUS REGLAS

    Devuelve el PRIMER error encontrado: mostrar cinco errores juntos
    en un campo abruma y no ayuda a arreglarlo.
*/
export const onValidateField = (value, rules = {}, values = {}) => {

    for (const [name, param] of Object.entries(rules)) {

        if (param === false || param === undefined || param === null) continue;

        const RUN = VALIDATORS[name];

        if (!RUN) {
            console.warn(`[useForm] No existe el validador "${name}".`);
            continue;
        }

        const ERROR = RUN(value, param, values);

        if (ERROR) return ERROR;
    }

    return null;
};
